import { jsx as _jsx } from "react/jsx-runtime";
import { motion, useMotionTemplate, useSpring, useTransform, } from 'motion/react';
import { memo } from 'react';
import { ChatSurface } from '../chat/ChatSurface.js';
import { DefaultDraggedContainer, Draggable, } from '../dnd/Draggable.js';
import { HelpSurface } from '../help/HelpSurface.js';
import { useIsTokenInHand } from './TokenHand.js';
import { useMaybeParentTokenSpace } from './TokenSpace.js';
import { useTokenData } from './types.js';
const tokenTags = ['token'];
const TokenDefault = memo(function TokenDefault({ children, data, className, handleProps, disableChat, id, helpContent, rulesId, name, chatSceneId = id, ...rest }) {
    const tokenData = useTokenData(id, data);
    const parent = useMaybeParentTokenSpace();
    const isInHand = parent?.type === 'hand';
    const activationConstraint = isInHand
        ? (ctx) => {
            return Math.abs(ctx.delta.y.get()) > 50;
        }
        : undefined;
    return (_jsx(Draggable, { ...rest, id: id, className: className, DraggedContainer: TokenContainer, data: tokenData, noHandle: true, tags: tokenTags, dropOnTag: "token-space", children: _jsx(ChatSurface, { disabled: disableChat, sceneId: chatSceneId, className: "w-full h-full", children: _jsx(HelpSurface, { disabled: !helpContent, content: helpContent, title: name, id: id, rulesId: rulesId, className: "w-full h-full", children: _jsx(Draggable.ConditionalHandle, { activationConstraint: activationConstraint, allowStartFromDragIn: isInHand, className: "w-full h-full", disabled: rest.noHandle, ...handleProps, children: children }) }) }) }));
});
export const Token = Object.assign(TokenDefault, {
    Handle: Draggable.Handle,
});
const TokenContainer = (props) => {
    const isInHand = useIsTokenInHand();
    if (isInHand) {
        return _jsx(TokenInHandContainer, { ...props });
    }
    return _jsx(DefaultDraggedContainer, { ...props });
};
// controls the animation of local, non-activated drag gestures
// according to how in-hand tokens should feel
const TokenInHandContainer = ({ children, draggable, gesture, ref, status, ...rest }) => {
    // freezes the X value until the gesture is activated
    const dampenedX = useTransform(() => {
        if (status === 'candidate') {
            return gesture.initialBounds.x + gesture.initialBounds.width / 2;
        }
        return gesture.current.x.get();
    });
    const distanceScale = useSpring(useTransform(() => {
        if (status === 'active') {
            if (gesture.type === 'keyboard') {
                // when dragging with the keyboard, we want to scale up a bit
                // to indicate drag
                return 1.1;
            }
            return 1;
        }
        const dist = Math.sqrt(gesture.delta.y.get() * gesture.delta.y.get());
        return 1.4 + dist / 50;
    }));
    const transform = useMotionTemplate `translate(-50%, -50%) translate3d(${dampenedX}px, ${gesture.current.y}px, 0) scale(${distanceScale})`;
    return (_jsx(motion.div, { style: {
            position: 'absolute',
            transform,
            zIndex: 1000000,
        }, ref: ref, ...rest, children: children }));
};
//# sourceMappingURL=Token.js.map